import React, { useState } from 'react';
//import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Fade,
  Slide,
  TextareaAutosize
} from '@mui/material';
import {
  Send,
  HelpOutline
} from '@mui/icons-material';
import Footer from '../shared/Footer';
import CaptchaSection from '../shared/CaptchaSection';
import CommonHeader from '../shared/CommonHeader';
import { submitQuestionForm } from '../../services/api/question.api';
import styles from '../landing/LandingPage.module.scss';

const AskQuestion = () => {
  // const navigate = useNavigate();
  const [formData, setFormData] = useState({
    question: '',
    name: '',
    email: '',
    organization: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  
  // CAPTCHA state
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, operator: '+', answer: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCaptchaChange = (value) => {
    setCaptchaAnswer(value);
    setCaptchaError(false);
  };

  const handleCaptchaGenerate = (newCaptcha) => {
    setCaptcha(newCaptcha);
    setCaptchaAnswer('');
    setCaptchaError(false);
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate CAPTCHA
    if (parseInt(captchaAnswer) !== captcha.answer) {
      setCaptchaError(true);
      showAlert('error', 'Please solve the CAPTCHA correctly.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await submitQuestionForm(formData);
      
      if (response.success) {
        showAlert('success', 'Thank you! Your question has been submitted successfully. We\'ll get back to you within 24 hours.');
        
        // Reset form
        setFormData({
          question: '',
          name: '',
          email: '',
          organization: ''
        });

        // Reset CAPTCHA
        setCaptchaAnswer('');
        setCaptchaError(false);
      } else {
        throw new Error(response.message || 'Failed to submit question');
      }
    } catch (error) {
      console.error('Question submission error:', error);
      showAlert('error', 'Sorry, there was an error submitting your question. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.landingPage}>
      {/* Header */}
      <CommonHeader showLoginButton={false} />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 8 }}>
        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            {/* Hero Section */}
            <Fade in={true} timeout={1000}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Slide direction="down" in={true} timeout={1200}>
                  <Box>
                    <HelpOutline 
                      sx={{ 
                        fontSize: 80, 
                        color: '#2259c9', 
                        mb: 3,
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.05)' },
                          '100%': { transform: 'scale(1)' }
                        }
                      }} 
                    />
                    <Typography 
                      variant="h2" 
                      component="h1" 
                      sx={{ 
                        fontWeight: 700,
                        color: '#2259c9',
                        mb: 3,
                        lineHeight: 1.2
                      }}
                    >
                      Ask a Question
                    </Typography>
                  </Box>
                </Slide>

                <Slide direction="up" in={true} timeout={1400}>
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        color: '#495057',
                        mb: 2,
                        fontWeight: 500,
                        lineHeight: 1.4
                      }}
                    >
                      Have any specific questions?
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: '1.1rem',
                        color: '#6c757d',
                        lineHeight: 1.6,
                        mb: 4
                      }}
                    >
                      We're here to help—whether you're a facility operator, clinician, or data lead.
                      Our team of experts is ready to answer any questions about our platform, implementation, or how we can customize solutions for your organization.
                    </Typography>
                  </Box>
                </Slide>
              </Box>
            </Fade>

            {/* Question Form */}
            <Fade in={true} timeout={1600}>
              <Paper 
                elevation={3}
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}
              >
                {alert.show && (
                  <Alert 
                    severity={alert.type} 
                    sx={{ mb: 3 }}
                    onClose={() => setAlert({ show: false, type: '', message: '' })}
                  >
                    {alert.message}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ position: 'relative' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            position: 'absolute',
                            top: formData.question ? '-8px' : '16px',
                            left: '12px',
                            backgroundColor: 'white',
                            padding: '0 8px',
                            color: formData.question ? '#2259c9' : '#6c757d',
                            fontWeight: formData.question ? 500 : 400,
                            fontSize: formData.question ? '12px' : '16px',
                            zIndex: 1,
                            transition: 'all 0.2s ease-in-out',
                            pointerEvents: 'none'
                          }}
                        >
                          Your Question *
                        </Typography>
                        <TextareaAutosize
                          name="question"
                          value={formData.question}
                          onChange={handleInputChange}
                          disabled={loading}
                          placeholder=""
                          minRows={4}
                          maxRows={10}
                          style={{
                            width: '100%',
                            padding: formData.question ? '24px 12px 8px 12px' : '16px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            fontSize: '16px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            backgroundColor: 'white',
                            outline: 'none',
                            transition: 'all 0.2s ease-in-out'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#2259c9';
                            e.target.style.borderWidth = '2px';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.borderWidth = '1px';
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        variant="outlined"
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        variant="outlined"
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Facility/Organization (Optional)"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        disabled={loading}
                        variant="outlined"
                        placeholder="Name of your facility or organization"
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>

                    {/* CAPTCHA Section */}
                    <Grid item xs={12}>
                      <CaptchaSection
                        captchaAnswer={captchaAnswer}
                        onCaptchaChange={handleCaptchaChange}
                        captchaError={captchaError}
                        loading={loading}
                        onCaptchaGenerate={handleCaptchaGenerate}
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                        className={styles.primaryCtaButton}
                        sx={{ 
                          px: 6, 
                          py: 2, 
                          fontSize: '1.1rem', 
                          minWidth: 200,
                          backgroundColor: '#2259c9',
                          '&:hover': {
                            backgroundColor: '#1a4a9e'
                          }
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Question'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Fade>

            {/* Support Information */}
            <Fade in={true} timeout={2000}>
              <Box sx={{ mt: 6, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#495057', mb: 2, fontWeight: 600 }}>
                  Need immediate assistance?
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">                  
                  <Button
                    variant="outlined"
                    href="mailto:customersupport@simplesnf.com"
                    sx={{ 
                      borderColor: '#2259c9', 
                      color: '#2259c9',
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                  >
                    Email: customersupport@simplesnf.com
                  </Button>
                </Stack>
                <Typography variant="body2" sx={{ color: '#6c757d', mt: 2 }}>
                  Business Hours: Monday - Friday, 8:00 AM - 6:00 PM EST
                </Typography>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AskQuestion; 